import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { useMemo, useState } from 'react'
import { ErrorState, Skeleton } from '../../ui/primitives.tsx'
import { AddColumn } from './AddColumn.tsx'
import { useColumns, useCreateTask, useMoveTask, useTasks, useUsers } from '../../hooks/useTasks.ts'
import { useHotkeys } from '../../hooks/useHotkeys.ts'
import { useIsLive } from '../../hooks/useRealtime.tsx'
import type { Task } from '../../lib/types.ts'
import { BoardColumn } from './BoardColumn.tsx'
import { TaskCard } from './TaskCard.tsx'
import { TaskDrawer } from './TaskDrawer.tsx'
import styles from './board.module.css'

const NO_FILTERS = {} as const

export function BoardView(): React.JSX.Element {
  const columns = useColumns()
  const users = useUsers()
  const tasks = useTasks(NO_FILTERS)
  const createTask = useCreateTask()
  const isLive = useIsLive()
  const moveTask = useMoveTask(NO_FILTERS, isLive)

  const [openTaskId, setOpenTaskId] = useState<number | null>(null)
  const [draggingId, setDraggingId] = useState<number | null>(null)
  // Only one quick-add is open at a time; `n` opens the first column's.
  const [quickAddColumnId, setQuickAddColumnId] = useState<number | null>(null)

  const byColumn = useMemo(() => groupByColumn(tasks.data ?? []), [tasks.data])

  useHotkeys({
    onNew: () => setQuickAddColumnId(columns.data?.[0]?.id ?? null),
    onEscape: () => {
      setOpenTaskId(null)
      setQuickAddColumnId(null)
    },
    openTaskId,
  })

  const sensors = useSensors(
    // A few pixels of slop, otherwise every click on a card starts a drag and never opens the drawer.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    // Drag is a key action, so it has to work without a mouse (ui-spec §11): Space lifts the card,
    // arrows move it, Space drops it, Esc cancels.
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  // Checked before the loading branch: a failed query is never pending, so the old order let a
  // dead board render as a permanently empty one instead of saying anything.
  if (columns.isError || tasks.isError) {
    return (
      <div className={styles.boardError}>
        <ErrorState
          text="Nie udało się wczytać boardu."
          onRetry={() => {
            void columns.refetch()
            void tasks.refetch()
          }}
        />
      </div>
    )
  }

  if (columns.isPending || tasks.isPending) {
    return (
      <div className={styles.board}>
        {[0, 200, 400].map((delay) => (
          <div key={delay} className={styles.column}>
            <div className={styles.columnBody}>
              <Skeleton delay={delay as 0 | 200 | 400} />
              <Skeleton delay={delay as 0 | 200 | 400} />
            </div>
          </div>
        ))}
      </div>
    )
  }

  const allColumns = columns.data ?? []
  const allUsers = users.data ?? []
  const allTasks = tasks.data ?? []

  const onDragStart = (event: DragStartEvent): void => {
    setDraggingId(Number(event.active.id))
  }

  const onDragEnd = (event: DragEndEvent): void => {
    setDraggingId(null)
    const { active, over } = event
    if (!over) return

    const activeId = Number(active.id)
    const moving = allTasks.find((t) => t.id === activeId)
    if (!moving) return

    // Dropped on the column background: append to that column.
    if (String(over.id).startsWith('column-')) {
      const columnId = Number(String(over.id).slice('column-'.length))
      if (columnId === moving.columnId) return
      moveTask.mutate({ id: activeId, columnId })
      return
    }

    const overId = Number(over.id)
    if (overId === activeId) return

    const target = allTasks.find((t) => t.id === overId)
    if (!target) return

    const inSameColumn = target.columnId === moving.columnId
    const siblings = byColumn.get(target.columnId) ?? []
    const movingIndex = siblings.findIndex((t) => t.id === activeId)
    const targetIndex = siblings.findIndex((t) => t.id === overId)

    // Dragging downwards inside one column means "after the target"; every other case is "before".
    const goesAfter = inSameColumn && movingIndex !== -1 && movingIndex < targetIndex

    moveTask.mutate({
      id: activeId,
      columnId: target.columnId,
      ...(goesAfter ? { afterTaskId: overId } : { beforeTaskId: overId }),
    })
  }

  const dragging = draggingId === null ? null : (allTasks.find((t) => t.id === draggingId) ?? null)

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={() => setDraggingId(null)}
      >
        <div className={styles.board}>
          {allColumns.map((column) => (
            <BoardColumn
              key={column.id}
              column={column}
              tasks={byColumn.get(column.id) ?? []}
              users={allUsers}
              quickAddOpen={quickAddColumnId === column.id}
              onQuickAddOpenChange={(open) => setQuickAddColumnId(open ? column.id : null)}
              onQuickAdd={(title, columnId) => createTask.mutate({ title, columnId })}
              onOpenTask={setOpenTaskId}
            />
          ))}

          <AddColumn />
        </div>

        <DragOverlay>
          {dragging ? (
            <TaskCard
              task={dragging}
              assignee={allUsers.find((u) => u.id === dragging.assigneeId) ?? null}
              onOpen={() => {}}
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      <TaskDrawer taskId={openTaskId} onClose={() => setOpenTaskId(null)} />
    </>
  )
}

function groupByColumn(tasks: Task[]): Map<number, Task[]> {
  const map = new Map<number, Task[]>()
  for (const task of tasks) {
    const list = map.get(task.columnId)
    if (list) list.push(task)
    else map.set(task.columnId, [task])
  }
  for (const list of map.values()) list.sort((a, b) => a.position - b.position)
  return map
}
